import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { z } from "@reflag/shared/utils/zod";

import { APICollectionResponse, APIEmptyResponse, APIResponse } from "./api";
import { AttributeFilter, AttributeFilterRuleSchema } from "./attributeFilter";
import {
  EnvironmentDTO,
  EnvironmentSelectionQuerySchema,
  EnvironmentSelectionQueryType,
} from "./environmentAPI";
import { FeatureConfigVersion } from "./featureConfigAPI";
import { FeatureViewDTO } from "./featureViewAPI";
import { UIFilter } from "./filter";
import { LinearTrackingEntitySchema } from "./linearConnectionAPI";
import { booleanish } from "./schemas/booleanish";
import { nameSchema } from "./schemas/nameSchema";
import { SlackChannelSchema } from "./schemas/shared";
import { SegmentDTO } from "./segmentAPI";
import { StageNameDTO } from "./stageAPI";
import { Paginated } from "./types/Paginated";
import { ReflagUser } from "./types/ReflagUser";
import { SummaryItem } from "./utils/summaryItem";

dayjs.extend(utc);

export const FeatureSourceSchema = z
  .enum(["event", "attribute"])
  .describe("The source of the flag");
export type FeatureSourceType = z.infer<typeof FeatureSourceSchema>;

export const SlackIntegrationSchema = z
  .object({
    slackChannel: SlackChannelSchema.nullish(),
    feedbackNotification: z.boolean().default(true),
    firstDataNotification: z.boolean().default(false),
    targetingRulesChangeNotification: z.boolean().default(true),
  })
  .strict();

export const LinearIntegrationSchema = z
  .object({
    trackingEntity: LinearTrackingEntitySchema,
    targetingRulesChangeNotification: z.boolean().default(true),
    feedbackNotification: z.boolean().default(false),
    autoSyncIcon: z.boolean().default(true),
  })
  .strict();

export type SlackIntegrationType = z.infer<typeof SlackIntegrationSchema>;
export type LinearIntegrationType = z.infer<typeof LinearIntegrationSchema>;

export const EventSelectorSchema = z.object({
  name: nameSchema.max(255),
  filter: z.array(AttributeFilterRuleSchema),
  system: z.boolean().optional(),
});

export type EventSelectorType = z.infer<typeof EventSelectorSchema>;

export const FeatureFeatureViewRelationsSchema = z.array(z.string().length(14));

export const FeatureFeedbackCampaignSchema = z.union([
  z.object({
    enabled: z.literal(false),
    question: z.string().max(255).optional(),
  }),
  z.object({
    enabled: z.literal(true),
    question: z
      .string()
      .min(1, { error: "Prompt question must be set" })
      .max(255),
  }),
]);

export const CreateFeatureArgsSchema = z
  .object({
    source: FeatureSourceSchema,
    name: nameSchema.describe("Name of the flag"),
    description: z
      .string()
      .max(8192)
      .nullish()
      .transform((v) => (v === "" ? null : v))
      .describe("Description of the flag"),
    icon: z.string().nullish().describe("Icon of the flag"),
    iconColor: z.string().nullish().describe("Hex color of the flag icon"),
    key: z
      .string()
      .min(1, { error: "Flag key is required" })
      .describe("Key of the flag"),
    secret: z.boolean().optional().describe("Whether the flag is secret"),
    stageId: z.string().length(14).optional().describe("Stage ID of the flag"),
    parentFeatureId: z
      .string()
      .nullish()
      .describe("Parent feature ID of the feature"),
    integrations: z
      .object({
        slack: SlackIntegrationSchema.partial().optional(),
        linear: LinearIntegrationSchema.partial().nullish(),
      })
      .strict()
      .optional(),
    featureViews: FeatureFeatureViewRelationsSchema.optional(),
    feedbackCampaign: FeatureFeedbackCampaignSchema.optional(),
    customEventSelectors: z.array(EventSelectorSchema).optional(),
    usingItAttributeFilter: z.array(AttributeFilterRuleSchema).optional(),
    isPermanent: z.boolean().optional().default(false),
    ownerUserId: z
      .string()
      .nullish()
      .transform((v) => (v === "" ? null : v)),
  })
  .strict();

const AdoptionEvaluationStrategySchema = z.enum([
  "legacy",
  "eventCount",
  "frequency",
]);

export type AdoptionEvaluationStrategy = z.infer<
  typeof AdoptionEvaluationStrategySchema
>;

export const EventPatchFeatureArgsSchema = z.object({
  adoptionEvaluationStrategy: AdoptionEvaluationStrategySchema.optional(),
  adoptionStrategyEventCountMinEventCount: z.number().min(1).optional(),
  adoptionStrategyFrequencyMinDaysCount: z.number().min(1).optional(),
  adoptionWindowSizeInDays: z.number().min(1).optional(),
});

const ArchivingDetailsPatchFeatureArgsSchema = z.object({
  isArchived: z.boolean().optional(),
  isCodebaseCleanedManually: z.boolean().optional(),
  autoArchive: z.boolean().optional(),
});

const OptInDetailsPatchFeatureArgsSchema = z.object({
  optInEnabled: z.boolean().optional(),
  optInDescription: z
    .string()
    .max(5000)
    .nullish()
    .transform((v) => (v === "" ? null : v)),
});

export type CreateFeatureArgsType = z.input<typeof CreateFeatureArgsSchema>;

export const PatchFeatureArgsSchema = CreateFeatureArgsSchema.omit({
  key: true,
})
  .extend(EventPatchFeatureArgsSchema.shape)
  .extend(ArchivingDetailsPatchFeatureArgsSchema.shape)
  .extend(OptInDetailsPatchFeatureArgsSchema.shape)
  .partial();

export type PatchFeatureArgsType = z.input<typeof PatchFeatureArgsSchema>;

export const FunnelStepList = [
  "company",
  "segment",
  "tried",
  "adopted",
  "retained",
] as const;

export type FunnelStep = (typeof FunnelStepList)[number];

export const FunnelStateList = [
  "never",
  "tried",
  "retained",
  "churned",
] as const;

export type FunnelState = (typeof FunnelStateList)[number];

export type FeatureGoal = {
  widgetId: string;
  featureId: string;
  metric: FeatureMetric;
  currentValue: number;
  threshold: number;
};

export type FeatureIntegrations = {
  slack: SlackIntegrationType;
  linear: LinearIntegrationType | null;
};

export type LinearCleanupIssueDetails = {
  id: string;
  updatedAt: string;
  identifier: string;
  title: string;
  state: { name: string; color: string; type: string } | null;
};

export type FeatureDetail = {
  id: string;
  key: string;
  parentFeatureId: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  iconColor: string | null;
  stage: StageNameDTO | null;
  integrations: FeatureIntegrations;
  linearCleanupIssueUrl: string | null;
  linearCleanupIssueDetails: LinearCleanupIssueDetails | null;
  segment?: SegmentDTO;
  featureViews: FeatureViewDTO[];
  configVersion: string | null;

  secret: boolean;
  isPermanent: boolean;
  isArchived: boolean;
  isStale: boolean;
  autoArchive: boolean;
  optInEnabled: boolean;
  optInDescription: string | null;
  owner: ReflagUser | null;

  source: FeatureSourceType;
  eventSelectors: EventSelectorType[];
  customEventSelectors: EventSelectorType[];
  adoptionEvaluationStrategy: AdoptionEvaluationStrategy;
  adoptionStrategyEventCountMinEventCount: number;
  adoptionStrategyFrequencyMinDaysCount: number;
  adoptionWindowSizeInDays: number;
  usingItAttributeFilter: AttributeFilter;
  autoFeedbackSurveysEnabled: boolean;
};

export type Entity = {
  id: string;
  name: string | null;
  avatarUrl?: string | null;
  isUnknown?: boolean;
};

export type FeatureListItem = {
  id: string;
  parentFeatureId: string | null;
  name: string;
  key: string;
  icon: string | null;
  iconColor: string | null;
  description: string | null;
  sortKey: string | null;
  source: FeatureSourceType;
  isArchived: boolean;
  isPermanent: boolean;
  isStale: boolean;
  stage: StageNameDTO | null;
  createdAt: string;
  autoFeedbackSurveysEnabled: boolean;
  hasCleanupPr: boolean;
  goals: FeatureGoal[];
  environmentRolloutTargetingRules: UIFilter[];
  environmentTargetingSummary: SummaryItem[] | null;
  rolledOutToEveryoneAt: string | null;
  pullRequests: PullRequest[];
  archivingChecks: ArchivingChecks | null;
  owner: ReflagUser | null;
} & FeatureListItemMetricsPart &
  FeatureListItemRolloutStatusPart &
  FeatureListItemRemoteConfigsPart;

export type FeatureListItemMetricsPart = {
  processingStatus: ProcessingStatus;
  feedbackCountAll: number | null;
  feedbackCountScored: number | null;
  feedbackCountRetained: number | null;
  feedbackCountRetainedAndScored: number | null;
  eventCount: number | null;
  triedCompaniesFraction: number | null;
  adoptedCompaniesFraction: number | null;
  retainedCompaniesFraction: number | null;
  satisfiedCompaniesFraction: number | null;
  adoptionRate: number | null;
  retentionRate: number | null;
  satisfactionRate: number | null;
  averageFrequency: number | null;
  currentMetrics?: STARSMetrics & { averageFrequency: number };
  rates?: STARSRates;
};

export const NullFeatureListItemMetricsPart: FeatureListItemMetricsPart = {
  processingStatus: "no-data",
  feedbackCountAll: null,
  feedbackCountScored: null,
  feedbackCountRetained: null,
  feedbackCountRetainedAndScored: null,
  eventCount: null,
  triedCompaniesFraction: null,
  adoptedCompaniesFraction: null,
  retainedCompaniesFraction: null,
  satisfiedCompaniesFraction: null,
  adoptionRate: null,
  retentionRate: null,
  satisfactionRate: null,
  averageFrequency: null,
};

export type FeatureListItemRolloutStatusPart = {
  rolloutEnvironment: Pick<
    EnvironmentDTO,
    "id" | "name" | "isProduction" | "order"
  > | null;
  latestCheck: string | null;
  latestUsage: string | null;
};

export const NullFeatureListItemRolloutStatusPart: FeatureListItemRolloutStatusPart =
  {
    rolloutEnvironment: null,
    latestCheck: null,
    latestUsage: null,
  };

export type FeatureListItemRemoteConfigsPart = {
  remoteConfigs: FeatureConfigVersion[];
};

export const NullFeatureListItemRemoteConfigsPart: FeatureListItemRemoteConfigsPart =
  {
    remoteConfigs: [],
  };

export type FeatureList = Paginated<
  FeatureListItem,
  FeatureListSortBy,
  { sortType: SortType; viewId: string | undefined }
>;

export interface STARSRates {
  /** Fraction of tried it companies that have adopted the feature. [0;1] */
  adoptionRate: number;
  /** Fraction of adopted companies that are retained for the feature. [0;1] */
  retentionRate: number;
  /** Satisfaction rate expressed as a fraction of the retained companies. [0;1] */
  satisfactionRate: number;
}

/**
 * Count of companies that have completed specific STARS funnel steps.
 *
 * All counts are inclusive, meaning that a step count includes a company
 * despite the company possibly having completed more advanced funnel steps.
 */
export interface STARSMetrics {
  /** The total number of companies tracked for the current App */
  company: number;
  /** The number of companies in the target segment of a Flag */
  segment: number;
  /** The number of companies that have met the "Tried it" criteria, including companies that have later advanced in the funnel */
  tried: number;
  /** The number of companies that have met the "Adopted" criteria, including companies that have later advanced in the funnel */
  adopted: number;
  /** The number of companies that have met the "Retained" criteria, including companies that have later advanced in the funnel */
  retained: number;
  /** The number of companies that have met the "Satisfied" criteria */
  satisfied: number;
}

export const FeatureMetricList = [
  "tried",
  "adopted",
  "retained",
  "satisfied",
  "adoptionRate",
  "retentionRate",
  "satisfactionRate",
  "triedCount",
  "adoptedCount",
  "retainedCount",
  "satisfiedCount",
  "averageFrequency",
  "feedbackCount",
] as const;
export type FeatureMetric = (typeof FeatureMetricList)[number];
export const FeatureMetricSchema = z.enum(FeatureMetricList);

export const GranularitySchema = z.enum(["minute", "hour", "day", "week"]);
export type Granularity = z.infer<typeof GranularitySchema>;

export interface FeatureMetricsHistoricalTimeseries {
  timeseries: {
    epoch: number;
    value: number | null;
  }[];
}

export const FrequencyMap = {
  0: "quarterly",
  1: "monthly",
  2: "weekly",
  3: "daily",
} as const;

export type FrequencyNumber = number | null;

export type FrequencyText =
  | (typeof FrequencyMap)[keyof typeof FrequencyMap]
  | "N/A";

export function frequencyRoundNumber(avgFrequencyNumber: number) {
  return Math.round(avgFrequencyNumber) as keyof typeof FrequencyMap;
}

export function frequencyNumberToText(
  avgFrequencyNumber: number | null,
): FrequencyText {
  if (avgFrequencyNumber === null) {
    return "N/A";
  }
  const intNum = frequencyRoundNumber(avgFrequencyNumber);
  return FrequencyMap[intNum];
}

export const SubsegmentQuerySchema = z.object({
  subsegment: z.string().length(14).optional(),
});

export const UseTargetingRulesSchema = z
  .object({
    useTargetingRules: booleanish.default(false),
  })
  .strict();

export type UseTargetingRulesType = z.input<typeof UseTargetingRulesSchema>;

export type SubsegmentQueryType = z.infer<typeof SubsegmentQuerySchema>;

export const FeatureMetricsHistoricalQuerySchema =
  EnvironmentSelectionQuerySchema.extend({
    startDate: z.iso
      .datetime()
      .optional()
      .default(dayjs.utc().startOf("day").subtract(30, "days").toISOString()),
    endDate: z.iso
      .datetime()
      .optional()
      .default(dayjs.utc().startOf("day").toISOString()),
    metric: FeatureMetricSchema,
  })
    .extend(UseTargetingRulesSchema.shape)
    .extend(SubsegmentQuerySchema.shape)
    .strict();
export type FeatureMetricsHistoricalQuery = z.input<
  typeof FeatureMetricsHistoricalQuerySchema
>;

export const RolloutStatusRequiringColumns = [
  "rolloutEnvironment",
  "latestCheck",
  "latestUsage",
] as const;
export const GoalsRequiringColumns = ["goals"] as const;
export const EnvironmentTargetingSummaryRequiringColumns = [
  "environmentRolloutTargetingRules",
] as const;

export const FeatureListSortByColumns = [
  ...[
    "name",
    "key",
    "description",
    "stage",
    "source",
    "autoFeedbackSurveysEnabled",
    "createdAt",
    "environmentRolloutTargetingRules",
    "rolledOutToEveryoneAt",
    "archivingChecks",
    "pullRequests",
    "owner",
  ],
  ...RolloutStatusRequiringColumns,
  ...GoalsRequiringColumns,
] as const;

// Legacy values from flag table - accepted but ignored (not exposed in OpenAPI)
const FeatureListSortByLegacyColumns = ["lastCheck", "lastTrack"] as const;

export const FeatureListSortBySchema = z.enum(FeatureListSortByColumns);
export type FeatureListSortBy = z.infer<typeof FeatureListSortBySchema>;

// Input schema that accepts both canonical and legacy values
const FeatureListSortByLegacySchema = z.enum(FeatureListSortByLegacyColumns);
export const FeatureListSortByInputSchema = z.union([
  FeatureListSortBySchema,
  FeatureListSortByLegacySchema,
]);
export type FeatureListSortByInput = z.infer<
  typeof FeatureListSortByInputSchema
>;

// Normalize legacy sortBy values to canonical ones
const featureSortByLegacyMapping: Record<string, FeatureListSortBy> = {
  lastCheck: "latestCheck",
  lastTrack: "latestUsage",
};

export function normalizeFeatureSortBy(
  sortBy: FeatureListSortByInput,
): FeatureListSortBy {
  return featureSortByLegacyMapping[sortBy] ?? sortBy;
}

export const FeatureListColumnSchema = z.enum([
  ...FeatureListSortByColumns,
  "cleanup",
]);
export type FeatureListColumn = z.infer<typeof FeatureListColumnSchema>;

export const sortTypeSchema = z.enum(["flat", "hierarchical"]);
export type SortType = z.infer<typeof sortTypeSchema>;

export const FeatureListQuerySchema = z
  .object({
    view: z.string().optional(),
    sortBy: FeatureListSortByInputSchema.optional().default("createdAt"),
    sortOrder: z.enum(["asc", "desc"]).optional().default("desc"),
    sortType: sortTypeSchema.optional().default("flat"),
    includeRolloutStatus: booleanish.optional().default(false),
    // deprecated, not used anymore but we keep it here for backwards compatibility
    includeProductionEstimatedTargetAudience: booleanish
      .optional()
      .default(false),
    includeGoals: booleanish.optional().default(false),
    includeRemoteConfigs: booleanish.optional().default(false),
    includeEnvironmentTargetingSummary: booleanish.optional().default(false),
  })
  .extend(EnvironmentSelectionQuerySchema.partial().shape)
  .strict()
  .refine(
    (v) => {
      const envRequired = v.includeRolloutStatus || v.includeGoals;
      return !envRequired || v.envId;
    },
    {
      message: "`envId` is required when including rollout status or goals",
      path: ["envId"],
    },
  );

export type FeatureListQuery = z.input<typeof FeatureListQuerySchema>;

export type FeatureName = {
  id: string;
  name: string;
  key: string;
  icon: string | null;
  iconColor: string | null;
  source: FeatureSourceType;
  isArchived: boolean;
  isPermanent: boolean;
  isStale: boolean;
  parentFeatureId: string | null;
};

export const FeatureNamesQuerySchema = z
  .object({
    viewId: z.string().optional(),
    eventIdentifier: z.string().optional(),
    filter: z
      .string()
      .trim()
      .optional()
      .describe(
        "Filter features by id, name, flag key, or description fallback",
      ),
    limit: z.coerce
      .number<number>()
      .int()
      .positive()
      .max(50)
      .optional()
      .describe("Maximum number of feature names to return"),
  })
  .extend(SubsegmentQuerySchema.shape);

export type FeatureNamesQueryType = z.infer<typeof FeatureNamesQuerySchema>;

export interface TrackingHealth {
  firstSeen: string | null;
  lastSeen: string | null;
  associatedCount1Week: number | null;
  notAssociatedCount1Week: number | null;
}

export type ProcessingStatus = "bootstrapping" | "no-data" | "has-data";

export type FeatureRolloutStatus = {
  environment: Pick<EnvironmentDTO, "id" | "name" | "isProduction" | "order">;
  latestCheck: string | null;
  latestUsage: string | null;
  latestFeedback: string | null;
};

export type FeatureRolloutStatusResponse = {
  stage: StageNameDTO | null;
  statuses: FeatureRolloutStatus[];
};

export const DEFAULT_FEATURE_ADOPTION_SETTINGS = {
  adoptionEvaluationStrategy: "frequency",
  adoptionWindowSizeInDays: 28,
  adoptionStrategyEventCountMinEventCount: 5,
  adoptionStrategyFrequencyMinDaysCount: 5,
};

export const LinearCleanupIssueSchema = z.object({
  details: z.custom<LinearCleanupIssueDetails>().nullable(),
  id: z.string(),
  url: z.string().url(),
  createdAt: z.string().datetime(),
});

export type LinearCleanupIssue = z.infer<typeof LinearCleanupIssueSchema>;

export type PullRequest = {
  id: string;
  status: "creating" | "open" | "closed" | "merged" | "error";
  number: number | null;

  url: string | null;
  title: string;

  createdAt: string;
  updatedAt: string;
};

export type ArchivingChecks = {
  rolledOutAt: string | null;
  rolledOutStatus: boolean;
  isStale: boolean;
  cleanupPrScheduledAt: string | null;
  codeRefsRemovalStatus: boolean;
  codeRefsScanCompleted: boolean;
  codeRefsMarkedClean: {
    user: ReflagUser | null;
    markedAt: string;
  } | null;
  lastCheckAt: string | null;
  lastCheckStatus: boolean;
  autoArchiveScheduledAt: string | null;
  githubIntegrationEnabled: boolean;
  isPermanent: boolean;
  isArchived: boolean;
  checksPassed: boolean;
};

export type AdoptionTimeseriesData = {
  epoch: number;
  value: number;
};

export type AdoptionChartData = {
  companies: number;
  users: number;
  timeseries: AdoptionTimeseriesData[];
};

export type FeatureAdoptionData = {
  exposed: AdoptionChartData;
  used: AdoptionChartData;
};

export const FeatureAdoptionQuerySchema = z.object({
  envId: z.string(),
  startTimestamp: z.string().datetime(),
  endTimestamp: z.string().datetime(),
  granularity: z.enum(["minute", "hour", "day", "week"]),
});

export type FeatureAdoptionQuery = z.input<typeof FeatureAdoptionQuerySchema>;

export interface FeatureAPI {
  "/apps/:appId/features": {
    GET: {
      response: APIResponse<FeatureList>;
      params: {
        appId: string;
      };
      query: FeatureListQuery;
    };
    POST: {
      body: CreateFeatureArgsType;
      response: APIResponse<{
        feature: FeatureDetail;
      }>;
      params: { appId: string };
    };
  };
  "/apps/:appId/features/names": {
    GET: {
      response: APIResponse<FeatureName[]>;
      params: {
        appId: string;
      };
      query: FeatureNamesQueryType;
    };
  };
  "/apps/:appId/features/keys": {
    GET: {
      response: APIResponse<string[]>;
      params: {
        appId: string;
      };
    };
  };
  "/apps/:appId/features/:featureId": {
    DELETE: {
      response: APIEmptyResponse;
      params: { appId: string; featureId: string };
    };
    GET: {
      response: APIResponse<{
        feature: FeatureDetail;
      }>;
      params: { appId: string; featureId: string };
      query: EnvironmentSelectionQueryType; // TODO ENV -- this is because we have env-specific settings in the body
    };
    PATCH: {
      response: APIResponse<{
        feature: FeatureDetail;
      }>;
      params: { appId: string; featureId: string };
      body: PatchFeatureArgsType;
      query: EnvironmentSelectionQueryType; // TODO ENV -- this is because we have env-specific settings in the body
    };
  };
  "/apps/:appId/features/:featureId/metrics/historical": {
    GET: {
      response: APIResponse<FeatureMetricsHistoricalTimeseries>;
      params: { appId: string; featureId: string };
      query: FeatureMetricsHistoricalQuery;
    };
  };
  "/apps/:appId/features/:featureId/metrics": {
    GET: {
      response: APIResponse<FeatureAdoptionData>;
      params: { appId: string; featureId: string };
      query: FeatureAdoptionQuery;
    };
  };
  "/apps/:appId/features/:featureId/views": {
    GET: {
      response: string[];
      params: { appId: string; featureId: string };
    };
    PUT: {
      response: string[];
      params: { appId: string; featureId: string };
      body: string[];
    };
  };
  "/apps/:appId/features/:featureId/rollout-status": {
    GET: {
      response: APIResponse<FeatureRolloutStatusResponse>;
      params: { appId: string; featureId: string };
    };
  };
  "/apps/:appId/features/:featureId/pull-requests": {
    GET: {
      response: APICollectionResponse<PullRequest>;
      params: { appId: string; featureId: string };
    };
  };
  "/apps/:appId/features/:featureId/linear-cleanup-issues": {
    GET: {
      response: APICollectionResponse<LinearCleanupIssue>;
      params: { appId: string; featureId: string };
    };
  };
  "/apps/:appId/features/:featureId/archiving-checks": {
    GET: {
      response: APIResponse<ArchivingChecks>;
      params: { appId: string; featureId: string };
    };
  };
}
